export type JanggiJudgementKind = 'best' | 'good' | 'inaccuracy' | 'mistake' | 'blunder';

export type JanggiJudgement = {
  kind: JanggiJudgementKind;
  label: string;
  loss: number;
  message: string;
};

export type JanggiJudgementInput = {
  move: string;
  bestMove?: string;
  beforeForMover: number;
  afterForMover: number;
  beforeWasMate: boolean;
  afterIsMateAgainstMover: boolean;
};

const comments: Record<JanggiJudgementKind, string[]> = {
  best: [
    '정수입니다. 엔진도 이 수부터 봤습니다. 기본수처럼 보여도 정확히 두는 게 제일 어렵습니다.',
    '이상한 거 없이 가장 센 수를 찾았습니다. 이런 수가 쌓이면 판이 편해집니다.',
    '상대 계획을 먼저 끊었습니다. 조용하지만 이런 게 진짜 기본수입니다.',
    '군더더기가 없습니다. 다음 수까지 연결되는 모양이 아주 깔끔합니다.',
  ],
  good: [
    '좋습니다. 최선수와 결은 조금 달라도 판의 중심은 놓치지 않았습니다.',
    '무리하지 않고 모양을 정리했습니다. 장기판이 한결 편해졌습니다.',
    '엔진도 고개를 끄덕일 정도입니다. 다음 수만 차분히 보시면 됩니다.',
    '실전적으로 충분히 좋은 선택입니다. 상대의 반격 자리도 잘 눌렀습니다.',
  ],
  inaccuracy: [
    '조금 아쉽습니다. 판이 터진 건 아니지만 더 깔끔한 기본수가 있었습니다.',
    '당장은 멀쩡해 보여도 상대에게 한 템포를 줬습니다. 살짝 이상한 거였습니다.',
    '큰 문제는 아닙니다. 다만 상대 최선 응수까지 보면 작은 손해가 보입니다.',
    '방향은 맞았지만 순서가 아쉽습니다. 먼저 상대의 강한 응수부터 지우는 편이 좋았습니다.',
  ],
  mistake: [
    '악수입니다. 상대가 정확히 받으면 주도권이 넘어갑니다.',
    '상대에게 참교육 각을 조금 열어줬습니다. 아직 수습은 가능합니다.',
    '기물보다 템포를 먼저 잃었습니다. 다음 수는 방어부터 보셔야 합니다.',
    '보기보다 손해가 큽니다. 바로 전 국면에서 장군과 잡는 수를 다시 비교해 보십시오.',
  ],
  blunder: [
    '큰 악수입니다. 상대가 놓치지 않으면 판의 방향이 크게 바뀝니다.',
    '상대에게 정답지를 건넨 수에 가깝습니다. 복기할 가치가 큰 장면입니다.',
    '외통이나 큰 기물 손실이 걸렸습니다. 바로 전 국면에서 다른 수를 찾아보십시오.',
    '이 수 한 번으로 버티던 모양이 무너졌습니다. 상대의 다음 장군부터 확인해 보셔야 합니다.',
  ],
};

const hash = (value: string) => Array.from(value).reduce((total, character) => ((total * 31) + character.charCodeAt(0)) >>> 0, 7);
const pick = (kind: JanggiJudgementKind, move: string) => comments[kind][hash(`${kind}:${move}`) % comments[kind].length];

export function judgeJanggiMove(input: JanggiJudgementInput): JanggiJudgement {
  const exactBest = Boolean(input.bestMove && input.move === input.bestMove);
  const rawLoss = input.beforeForMover - input.afterForMover;
  const loss = exactBest ? 0 : Math.max(0, Math.min(9999, Math.round(rawLoss)));

  let kind: JanggiJudgementKind = exactBest || loss <= 18
    ? 'best'
    : loss <= 42
      ? 'good'
      : loss <= 105
        ? 'inaccuracy'
        : loss <= 245
          ? 'mistake'
          : 'blunder';

  if (input.afterIsMateAgainstMover) kind = 'blunder';
  else if (input.beforeWasMate && !exactBest && loss > 40) kind = 'mistake';

  const label: Record<JanggiJudgementKind, string> = {
    best: '정수',
    good: '좋은 수',
    inaccuracy: '조금 아쉬움',
    mistake: '악수',
    blunder: '큰 악수',
  };

  let message = pick(kind, input.move);
  if (input.afterIsMateAgainstMover) message = '상대에게 강제 외통 수순을 허용했습니다. 도주·차단·잡기를 다시 세어보십시오.';
  else if (input.beforeWasMate && !exactBest && loss > 40) message = '잡고 있던 외통 수순을 놓쳤습니다. 바로 전 국면에서 장군 후보부터 다시 보십시오.';
  else if (exactBest && input.beforeWasMate) message = '외통 수순을 정확히 유지했습니다. 이제 급하게만 두지 않으면 됩니다.';
  else if (exactBest && input.beforeForMover < -180) message = '불리하지만 가장 질긴 수를 찾았습니다. 이런 버티기가 역전 기회를 만듭니다.';

  return { kind, label: label[kind], loss, message };
}
